import React, { Fragment } from 'react';
import { formatApiTime } from 'Utils/date/apiDate';
import { t } from 'Src/i18n';
import { mapClosingDays } from 'Components/widgets/childcare-summary/ChildcareSummary.utils';
import { BABY_CM_CHILDCARE_MIN_AGE, PETIT_CM_CHILDCARE_MIN_AGE } from './childcare.consts';
export const isBabyClubMed = (childcare: UpsellChildcare) => {
  const childcareMinAge = childcare.productInfo.ageInMonths.min;
  return childcareMinAge === BABY_CM_CHILDCARE_MIN_AGE;
};
export const isPetitClubMed = (childcare: UpsellChildcare) => {
  const childcareMinAge = childcare.productInfo.ageInMonths.min;
  return childcareMinAge === PETIT_CM_CHILDCARE_MIN_AGE;
};
export const isBabyOrPetitCM = (childcare: UpsellChildcare) => {
  return isBabyClubMed(childcare) || isPetitClubMed(childcare);
};
declare type DurationSlotsLabelsType = {
  timeSlots: ReadonlyArray<{
    opening: string;
    closing: string;
    type: string;
  }>;
  rentedOrPaidTimeSlots?: ReadonlyArray<{
    slotName: string;
  }>;
  showOnlyRentedOrPaid?: boolean;
  closingWeekdays: ReadonlyArray<string>;
  classNames?: string;
  isPetitCM?: boolean;
};

const getClosingDays = (closingWeekdays): string => {
  if (!closingWeekdays?.length) {
    return '';
  }

  // eslint-disable-next-line camelcase
  const closing_weekdays = closingWeekdays.map(mapClosingDays).join(', ');
  return t('booking.services.childcare.closingDays', {
    closing_weekdays,
  });
};

export const DurationSlotsLabels = ({
  timeSlots,
  closingWeekdays,
  rentedOrPaidTimeSlots = [],
  showOnlyRentedOrPaid = false,
  classNames = '',
  isPetitCM,
}: DurationSlotsLabelsType) => {
  const keySlots = {
    MORNING: 'booking.services.childcare.timeslotMorning',
    AFTERNOON: 'booking.services.childcare.timeslotAfternoon',
    DAY: 'booking.services.childcare.timeslotAllDay',
    EVENING: isPetitCM
      ? 'booking.services.childcare.timeslotEvening'
      : 'booking.services.childcare.timeslotEvening2',
  };
  const closedDaysLabel = getClosingDays(closingWeekdays);

  if (timeSlots?.length === 0) {
    return closedDaysLabel || '';
  }

  return (
    <p className={classNames}>
      {timeSlots.map((timeSlot) => {
        return rentedOrPaidTimeSlots?.some(({ slotName }) => slotName === timeSlot.type) ||
          !showOnlyRentedOrPaid ? (
          <Fragment key={`${timeSlot.type}-${timeSlot.opening}`}>
            <span>
              {t(keySlots[timeSlot.type], {
                opening: formatApiTime(timeSlot.opening, 'LT').toLowerCase(),
                closing: formatApiTime(timeSlot.closing, 'LT').toLowerCase(),
              })}
            </span>
            <br />
          </Fragment>
        ) : null;
      })}
      {closedDaysLabel && <span>{closedDaysLabel}</span>}
    </p>
  );
};
export const displayChildcaresNames = (
  selectedProposal: Proposal,
  childcares: ReadonlyArray<UpsellChildcare>,
) => {
  const childcaresAttendees = childcares.filter((childcare) => {
    const currentAttendee = childcare.pricingInfo.attendees.find(
      ({ id }) => id === childcare.pricingInfo.childId,
    );
    const currentAttendeeHasRentedOrIncluded =
      (currentAttendee?.rentedAmount?.value && currentAttendee.rentedAmount.value > 0) ||
      childcare.productInfo.isIncluded;
    return currentAttendeeHasRentedOrIncluded;
  });
  const finalLabel = childcaresAttendees.reduce((composedLabel, newChildcare, index) => {
    return composedLabel + `${index > 0 ? ' | ' : ''} 1 ${newChildcare.productInfo.label}`;
  }, '');
  return finalLabel;
};
export function addOrRemoveChildcareFromCart({
  childcareCart,
  attendeeId,
  childcareId,
  startDate,
  endDate,
  bookingId,
  updateCart,
  appContext,
}: {
  childcareCart?: CartChildcare;
  attendeeId: string;
  childcareId: string;
  startDate: string;
  endDate: string;
  bookingId: string;
  updateCart: (arg0: any) => Promise<any>;
  appContext: AppContext;
}) {
  const attendees = ((childcareCart && childcareCart.attendees) || []).map(({ id }) => ({
    id,
  }));
  const index = attendees.findIndex(({ id }) => id === attendeeId);

  if (index !== -1 && attendees.length === 1) {
    return updateCart({
      id: childcareId,
      bookingId,
      context: appContext,
      action: 'remove',
      serviceName: 'CHILDCARE',
    });
  }

  if (index !== -1) {
    attendees.splice(index, 1);
  } else {
    attendees.push({
      id: attendeeId,
    });
  }

  const schedules = [
    {
      start_date: startDate,
      end_date: endDate,
      attendees,
    },
  ];
  return updateCart({
    id: childcareId,
    bookingId,
    schedules,
    context: appContext,
    action: 'update',
    serviceName: 'CHILDCARE',
  });
}
